import { useNavigate } from 'react-router-dom';
import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Rocket, ArrowRight, X, Sparkles } from 'lucide-react';

const SUNSET = '#FF6B35';

/**
 * UpgradeModal — the in-app paywall interception.
 *
 * Rendered globally via UpgradeModalProvider and opened by usePaidGate() the
 * moment an unpaid user (resolvePaidStatus === false) clicks any gated control
 * (Upload File, Create Portal, Copy Link, Rename, Brand, Delete). The gated
 * action never runs — no OS file picker, no clipboard write, no toast.
 *
 * CTA routes to /pricing.
 */
export default function UpgradeModal({ open, onClose, seatsLeft, seatsTotal }) {
    const navigate = useNavigate();

    const goPricing = () => {
        onClose?.();
        navigate('/pricing');
    };

    const hasSeats = Number.isFinite(seatsLeft);
    const total = Number.isFinite(seatsTotal) ? seatsTotal : 1000;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                data-testid="upgrade-modal"
                hideClose
                className="max-w-md p-0 gap-0 overflow-hidden border border-[#FF6B35]/40 bg-card shadow-2xl shadow-[#FF6B35]/10"
            >
                <button
                    data-testid="upgrade-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                    className="group absolute right-5 top-5 z-10 h-9 w-9 grid place-items-center rounded-full bg-background/80 backdrop-blur-sm border border-border text-foreground/70 hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all duration-200 hover:rotate-90 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B35]/60"
                >
                    <X className="h-4 w-4" strokeWidth={2.4} />
                </button>

                {/* Sunset header band */}
                <div
                    className="px-8 pt-10 pb-7 relative"
                    style={{ background: `linear-gradient(180deg, ${SUNSET}14 0%, transparent 100%)` }}
                >
                    <div className="flex items-center gap-2.5">
                        <span
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
                            style={{ backgroundColor: `${SUNSET}18`, borderColor: `${SUNSET}55` }}
                        >
                            <Rocket className="h-4 w-4" style={{ color: SUNSET }} strokeWidth={2.2} />
                        </span>
                        <p
                            className="text-[10px] uppercase tracking-[0.32em] font-medium"
                            style={{ color: SUNSET }}
                        >
                            Founding Beta · Members Only
                        </p>
                    </div>

                    <DialogTitle className="mt-5 font-heading text-[26px] sm:text-[28px] leading-[1.15] tracking-tight">
                        Wanna try? Grab a seat! 🚀
                    </DialogTitle>
                    <DialogDescription className="mt-4 text-[14px] leading-[1.65] text-foreground/85">
                        This action is reserved for paid members. Claim one of the
                        limited founding seats to unlock uploads, portals, sharing,
                        and full white-label branding.
                    </DialogDescription>
                </div>

                {/* Perk row + live seats-left urgency */}
                <div className="px-8">
                    <div
                        className="flex items-start gap-3 rounded-lg border px-4 py-3.5"
                        style={{ backgroundColor: `${SUNSET}08`, borderColor: `${SUNSET}33` }}
                    >
                        <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SUNSET }} />
                        <p className="text-[13px] leading-relaxed text-foreground/85">
                            One flat founding price. Lock in your seat before the {total.toLocaleString()}
                            {' '}beta slots fill up.
                        </p>
                    </div>
                    {hasSeats && (
                        <div
                            data-testid="upgrade-modal-seats-left"
                            className="mt-3 flex items-center justify-between rounded-lg border px-4 py-3"
                            style={{ borderColor: `${SUNSET}55`, backgroundColor: `${SUNSET}0D` }}
                        >
                            <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                                Founding seats left
                            </span>
                            <span
                                data-testid="upgrade-modal-seats-value"
                                className="font-heading text-2xl"
                                style={{ color: SUNSET }}
                            >
                                {Math.max(0, seatsLeft).toLocaleString()}
                                <span className="text-muted-foreground text-sm font-normal"> / {total.toLocaleString()}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* CTA */}
                <div className="px-8 pt-6 pb-8">
                    <Button
                        data-testid="upgrade-modal-cta"
                        onClick={goPricing}
                        className="group w-full h-12 text-white font-medium tracking-[0.02em] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
                        style={{ backgroundColor: SUNSET }}
                        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 8px 28px ${SUNSET}55`)}
                        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = `0 0 0 0 ${SUNSET}00`)}
                    >
                        See plans &amp; grab a seat
                        <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                    <button
                        data-testid="upgrade-modal-maybe-later"
                        onClick={onClose}
                        className="mt-3 w-full text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Maybe later
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
